import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '../lib/api'
import type { EmptyStateCopy, EmptyStateKind } from '../lib/emptyState'
import { TableSkeleton } from './Skeleton'

export interface AsyncState<TValue> {
  value: TValue | null
  error: string | null
  loading: boolean
  reload: () => void
}

/**
 * Loads data and keeps the last good value while reloading.
 *
 * Blanking the screen on every refresh is the single most common way a list view becomes unpleasant
 * to use: an auto-refreshing event stream would flash empty every few seconds. Keeping the previous
 * value means a reload is invisible unless it changes something.
 */
export function useAsync<TValue>(load: () => Promise<TValue>, dependencies: unknown[] = []): AsyncState<TValue> {
  const [value, setValue] = useState<TValue | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [nonce, setNonce] = useState(0)
  const alive = useRef(true)

  useEffect(() => {
    alive.current = true
    return () => {
      alive.current = false
    }
  }, [])

  useEffect(() => {
    setLoading(true)
    load()
      .then((next) => {
        if (!alive.current) return
        setValue(next)
        setError(null)
      })
      .catch((failure: unknown) => {
        if (!alive.current) return
        setError(failure instanceof ApiError ? failure.message : 'Something went wrong loading this page.')
      })
      .finally(() => {
        if (alive.current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies, nonce])

  const reload = useCallback(() => setNonce((current) => current + 1), [])
  return { value, error, loading, reload }
}

export function ErrorBanner({ message }: { message: string | null }) {
  if (!message) return null
  return (
    <div className="banner error" role="alert">
      {message}
    </div>
  )
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="empty">{children}</div>
}

const SEVERITY_NAMES: Record<number, string> = {
  0: 'Unknown',
  1: 'Informational',
  2: 'Low',
  3: 'Medium',
  4: 'High',
  5: 'Critical',
  6: 'Fatal'
}

export function SeverityPill({ severityId }: { severityId: number }) {
  return (
    <span className={`pill sev-${severityId}`}>{SEVERITY_NAMES[severityId] ?? `Severity ${severityId}`}</span>
  )
}

export function formatUtc(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? '—' : parsed.toLocaleString()
}

/**
 * The one component every list uses to say what it is currently saying.
 *
 * Six states rather than two, because an empty table with headers is the same picture for six
 * situations that need six different responses. Which one applies is decided in `lib/emptyState.ts`
 * so that every screen gets the rule rather than each screen getting it right separately - and the
 * rule that matters is that a read which did not arrive is an error, never good news (D-263).
 *
 * Layout only. Everything it is allowed to say comes from the copy tables beside that decision.
 */
export function ListState({
  kind,
  copy,
  onAction,
  columns
}: {
  kind: EmptyStateKind
  copy: Record<Exclude<EmptyStateKind, 'rows'>, EmptyStateCopy>
  onAction?: () => void
  columns?: number
}) {
  if (kind === 'rows') return null
  if (kind === 'loading') {
    return (
      <>
        <span className="visually-hidden" role="status">
          {copy.loading.message}
        </span>
        <TableSkeleton columns={columns} />
      </>
    )
  }

  const { message, hint, action } = copy[kind]

  return (
    // Good news is not drawn as a problem. It is the state most easily got wrong and the one an
    // operator most deserves to read plainly.
    <div className={kind === 'good-news' ? 'empty good-news' : 'empty'} data-testid={`state-${kind}`}>
      {message}
      {hint && <div className="hint">{hint}</div>}
      {action && onAction && (
        <div className="toolbar">
          <button type="button" onClick={onAction}>
            {action}
          </button>
        </div>
      )}
    </div>
  )
}
